import {
    ApplicationRef,
    ComponentRef,
    createComponent,
    EmbeddedViewRef,
    EnvironmentInjector,
    inject,
    Injectable,
    Injector,
    OutputRefSubscription,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { DrawerComponent } from './drawer.component';

export type DrawerPlacement = 'left' | 'right' | 'center';

export interface DrawerConfig {
    placement?: DrawerPlacement;
    lightDismiss?: boolean;
    backdrop?: boolean;
    canResize?: boolean;
    width?: string;
}

export interface DrawerRef {
    close(): void;
    toggle(): void;
    open(): void;
}

@Injectable({
    providedIn: 'root',
})
export class DrawerService {
    readonly #appRef = inject(ApplicationRef);
    readonly #envInjector = inject(EnvironmentInjector);
    #drawerInstances = new Map<number, { componentRef: ComponentRef<DrawerComponent>; embeddedView: EmbeddedViewRef<unknown>; subscription: OutputRefSubscription }>();
    #idCounter = 0;
    #outletElement: HTMLElement | null = null;

    public registerOutlet(element: HTMLElement) {
        this.#outletElement = element;
    }

    public unregisterOutlet(element: HTMLElement) {
        if (this.#outletElement === element) {
            this.#outletElement = null;
        }
    }

    public create(
        template: TemplateRef<unknown>,
        config?: DrawerConfig,
        viewContainerRef?: ViewContainerRef,
        onClose?: () => void,
    ): DrawerRef {
        const id = this.#idCounter++;
        this.#createDrawer(id, template, config, viewContainerRef, onClose);

        return {
            close: () => {
                const inst = this.#drawerInstances.get(id);
                inst?.componentRef.instance.close();
            },
            toggle: () => {
                const inst = this.#drawerInstances.get(id);
                inst?.componentRef.instance.toggle();
            },
            open: () => {
                const inst = this.#drawerInstances.get(id);
                inst?.componentRef.instance.open();
            },
        };
    }

    #createDrawer(
        id: number,
        template: TemplateRef<unknown>,
        config?: DrawerConfig,
        viewContainerRef?: ViewContainerRef,
        onClose?: () => void,
    ): ComponentRef<DrawerComponent> {
        const injector = viewContainerRef?.injector ?? this.#envInjector;

        const embeddedView = template.createEmbeddedView({});
        this.#appRef.attachView(embeddedView);

        const componentRef = createComponent(DrawerComponent, {
            environmentInjector: this.#envInjector,
            elementInjector: injector as Injector,
            projectableNodes: [embeddedView.rootNodes],
        });

        const { placement, lightDismiss, backdrop, canResize, width } = config ?? {};

        if (placement) {
            componentRef.instance.placement.set(placement);
        }

        if (lightDismiss !== undefined) {
            componentRef.instance.lightDismiss.set(lightDismiss);
        }

        if (backdrop !== undefined) {
            componentRef.instance.showBackdrop.set(backdrop);
        }

        if (canResize !== undefined) {
            componentRef.instance.canResize.set(canResize);
        }

        if (width !== undefined) {
            componentRef.instance.width.set(width);
        }

        this.#appRef.attachView(componentRef.hostView);

        const hostElement = componentRef.location.nativeElement as HTMLElement;
        const container = this.#outletElement ?? document.body;
        container.appendChild(hostElement);

        const subscription = componentRef.instance.closed.subscribe(() => {
            onClose?.();
            this.#destroy(id);
        });

        this.#drawerInstances.set(id, { componentRef, embeddedView, subscription });

        componentRef.changeDetectorRef.detectChanges();
        componentRef.instance.open();

        return componentRef;
    }

    #destroy(id: number) {
        const instance = this.#drawerInstances.get(id);

        if (!instance) return;

        const { componentRef, embeddedView, subscription } = instance;
        this.#drawerInstances.delete(id);

        subscription.unsubscribe();

        this.#appRef.detachView(embeddedView);
        embeddedView.destroy();

        this.#appRef.detachView(componentRef.hostView);
        componentRef.location.nativeElement.remove();
        componentRef.destroy();
    }
}
